import { Link } from 'react-router-dom';
import { SectionHeading } from '../components';

const TermsConditions = () => {
  return (
    <div className="bg-background">
      {/* ===== SECTION 1: PAGE HERO ===== */}
      <section className="relative py-24 lg:py-32 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-15" style={{ backgroundImage: "url('/images/goldson-wade-2018-hero.jpg')" }} />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800/90 to-primary-dark/85" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/20 border border-primary/30 mb-6">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            <span className="text-xs font-bold tracking-widest uppercase text-white">Legal</span>
          </span>
          <h1 className="heading-hero text-white mb-6 max-w-4xl mx-auto">Terms & Conditions</h1>
          <p className="text-lg text-slate-200 leading-relaxed max-w-2xl mx-auto">
            Please read these terms carefully before using this site or registering for the Goldson-Wade Family Reunion.
          </p>
        </div>
      </section>

      {/* ===== LAST UPDATED ===== */}
      <section className="py-8 bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm text-slate-500">Last updated: March 2026</p>
        </div>
      </section>

      {/* ===== SECTION 2: AGREEMENT TO TERMS ===== */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Section 1" title="Agreement to Terms" />
          <div className="prose prose-slate max-w-none">
            <p className="text-slate-600 leading-relaxed text-lg">
              By accessing or using the Goldson-Wade Family Reunion website, you agree to be bound by these Terms & Conditions. If you do not agree with any part of these terms, please do not use this site or register for the reunion.
            </p>
            <p className="text-slate-600 leading-relaxed">
              These terms apply to all visitors, users, and registered members of the site. Your continued use of the website following the posting of any changes to these terms constitutes acceptance of those changes.
            </p>
          </div>
        </div>
      </section>

      {/* ===== SECTION 3: EVENT REGISTRATION ===== */}
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Section 2" title="Event Registration" />
          <div className="prose prose-slate max-w-none">
            <p className="text-slate-600 leading-relaxed text-lg">
              By completing the registration process, you confirm that the information you provide is accurate and complete. Registration constitutes your commitment to participate in the Goldson-Wade Family Reunion, subject to availability and these terms.
            </p>
            <p className="text-slate-600 leading-relaxed">
              You are responsible for ensuring that all family members listed in your registration are aware of and agree to these terms. Group leaders are responsible for the accuracy of all information submitted on behalf of their family unit.
            </p>
          </div>
        </div>
      </section>

      {/* ===== SECTION 4: PAYMENT PROCESSING ===== */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Section 3" title="Payment Processing" />
          <div className="prose prose-slate max-w-none">
            <p className="text-slate-600 leading-relaxed text-lg">
              All payments for the Goldson-Wade Family Reunion are processed through and managed by <strong className="text-slate-900">KG ProDesign LLC</strong>. KG ProDesign LLC is responsible for tracking and managing all funds collected for the overall reunion budget.
            </p>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 my-6">
              <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wide mb-3">Important Payment Information</h4>
              <ul className="space-y-2 text-slate-600">
                <li>Your credit card statement will show charges from <strong className="text-slate-900">"KG PRODESIGN LLC"</strong> or <strong className="text-slate-900">"KGP"</strong></li>
                <li>Service fees and processing fees may apply when using credit card or payment gateway services</li>
                <li>KG ProDesign LLC tracks and manages all reunion funds on behalf of the organizing committee</li>
              </ul>
            </div>
            <p className="text-slate-500 text-sm italic">
              Please be aware that third-party payment processors may charge additional service fees or processing fees. These fees are separate from reunion costs and are determined by the payment provider.
            </p>
          </div>
        </div>
      </section>

      {/* ===== SECTION 5: CANCELLATION & REFUNDS ===== */}
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Section 4" title="Cancellation & Refunds" />
          <div className="prose prose-slate max-w-none">
            <p className="text-slate-600 leading-relaxed text-lg">
              Participants may request a full refund for cancellation requests made 90 or more days before the event start date. After the 90-day window, refunds may be partial or unavailable depending on timing and costs already incurred.
            </p>
            <p className="text-slate-600 leading-relaxed">
              For complete details on our cancellation and refund procedures, please review our{' '}
              <Link to="/cancellation-policy" className="text-primary font-medium hover:underline">
                Cancellation & Refund Policy
              </Link>.
            </p>
          </div>
        </div>
      </section>

      {/* ===== SECTION 6: USER CONDUCT ===== */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Section 5" title="User Conduct" />
          <div className="prose prose-slate max-w-none">
            <p className="text-slate-600 leading-relaxed text-lg">
              The Goldson-Wade Family Reunion is a family-friendly event. All participants are expected to conduct themselves with respect, courtesy, and consideration for others throughout the reunion and when using this website.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Organizers reserve the right to remove any participant whose behavior is disruptive, disrespectful, or endangers the safety and well-being of other attendees. In such cases, refunds are not guaranteed.
            </p>
          </div>
        </div>
      </section>

      {/* ===== SECTION 7: LIMITATION OF LIABILITY ===== */}
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Section 6" title="Limitation of Liability" />
          <div className="prose prose-slate max-w-none">
            <p className="text-slate-600 leading-relaxed text-lg">
              KG ProDesign LLC and the Goldson-Wade Family Reunion organizers shall not be held liable for personal injury, property loss or damage, travel disruptions, weather-related cancellations, or any other unforeseen circumstances that may arise before, during, or after the reunion event.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Participants attend the reunion at their own risk and are encouraged to obtain appropriate travel and health insurance for the duration of the event.
            </p>
          </div>
        </div>
      </section>

      {/* ===== SECTION 8: CHANGES TO TERMS ===== */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Section 7" title="Changes to Terms" />
          <div className="prose prose-slate max-w-none">
            <p className="text-slate-600 leading-relaxed text-lg">
              We reserve the right to update or modify these Terms & Conditions at any time. Changes will be posted on this page with an updated revision date. Your continued use of the site following any changes constitutes your acceptance of the revised terms.
            </p>
            <p className="text-slate-600 leading-relaxed">
              We encourage you to review these terms periodically. For significant changes, we may send email notifications to registered users.
            </p>
          </div>
        </div>
      </section>

      {/* ===== SECTION 9: CONTACT INFORMATION ===== */}
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Section 8" title="Contact Information" />
          <div className="prose prose-slate max-w-none">
            <p className="text-slate-600 leading-relaxed text-lg">
              If you have any questions about these Terms & Conditions, please contact us:
            </p>
            <div className="bg-white border border-slate-200 rounded-lg p-6 my-6">
              <p className="text-slate-900 font-bold text-lg mb-2">KG ProDesign LLC</p>
              <p className="text-slate-600">
                Website:{' '}
                <a href="https://kgprodesign.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  kgprodesign.com
                </a>
              </p>
              <p className="text-slate-600">
                Email:{' '}
                <a href="mailto:info@kgprodesign.com" className="text-primary hover:underline">
                  info@kgprodesign.com
                </a>
              </p>
              <p className="text-sm text-slate-500 mt-3">
                For payment-related inquiries, please reference "KG PRODESIGN LLC" or "KGP" as it appears on your credit card statement.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CROSS-LINKS ===== */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-lg font-bold text-slate-900 mb-4">Related Policies</h3>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/privacy-policy"
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 hover:text-primary transition-colors"
            >
              Privacy Policy
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
            <Link
              to="/cancellation-policy"
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 hover:text-primary transition-colors"
            >
              Cancellation & Refund Policy
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsConditions;
